// client/src/components/leagues/MyLeagues.js
import { useNavigate } from 'react-router-dom'
import PageLayout from '../common/layout/PageLayout'
import useMyLeagues from '../../hooks/useMyLeagues'
import './MyLeagues.css'

function MyLeagues() {
  const navigate = useNavigate()
  const { leagues, loading } = useMyLeagues()

  const goToLeague = (id) => navigate(`/leagues/${id}`)

  const getStatusLabel = (league) => {
    switch (league.league_status) {
      case 'draft':
        return 'Draft'
      case 'registration_open':
        return 'Registration Open'
      case 'registration_closed':
        return 'Registration Closed'
      case 'pending_start':
        return 'Starting Soon'
      case 'active':
      case 'in_progress':
        return 'In Progress'
      case 'playoffs':
        return 'Playoffs'
      case 'completed':
        return 'Completed'
      case 'canceled':
        return 'Canceled'
      default:
        return 'Unknown'
    }
  }

  const getStatusColor = (league) => {
    switch (league.league_status) {
      case 'draft':
        return '#6c757d' // secondary
      case 'registration_open':
        return '#17a2b8' // info
      case 'registration_closed':
        return '#f0ad4e' // warning
      case 'pending_start':
        return '#007bff' // primary
      case 'active':
      case 'in_progress':
        return '#28a745' // success
      case 'playoffs':
        return '#17a2b8' // info
      case 'completed':
        return '#343a40' // dark
      case 'canceled':
        return '#dc3545' // danger
      default:
        return '#6c757d' // secondary
    }
  }

  const canAccessLeague = (league) => {
    return ['active', 'in_progress', 'playoffs', 'completed'].includes(
      league.league_status,
    )
  }

  return (
    <PageLayout title="My Leagues">
      <div className="content-card">
        {loading ? (
          <div
            role="status"
            aria-live="polite"
            aria-busy="true"
            style={{ textAlign: 'center', padding: '2rem' }}
          >
            <p>Loading your leagues...</p>
          </div>
        ) : leagues.length > 0 ? (
          <div className="leagues-grid">
            {leagues.map((league) => (
              <div
                key={league.league_id}
                className="league-card"
                data-testid="league-card"
                onClick={() =>
                  canAccessLeague(league) && goToLeague(league.league_id)
                }
                style={{
                  cursor: canAccessLeague(league) ? 'pointer' : 'default',
                }}
              >
                <div className="league-card-content">
                  <h3>{league.league_name}</h3>
                  <div
                    className="league-status"
                    style={{
                      color: getStatusColor(league),
                      fontWeight: 500,
                    }}
                  >
                    {getStatusLabel(league)}
                  </div>
                  {canAccessLeague(league) ? (
                    <div className="league-action">
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          goToLeague(league.league_id)
                        }}
                        data-track="navigation.view_league_details"
                      >
                        View League Details →
                      </button>
                    </div>
                  ) : (
                    <div className="league-action">
                      <small className="text-muted">
                        {league.league_status === 'registration_open'
                          ? 'Registration in progress'
                          : league.league_status === 'registration_closed'
                            ? 'Divisions being finalized'
                            : league.league_status === 'pending_start'
                              ? 'League starting soon'
                              : league.league_status === 'canceled'
                                ? 'League canceled'
                                : 'Waiting to start'}
                      </small>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="no-leagues-message"
            data-testid="no-leagues-message"
            style={{ textAlign: 'center', padding: '2rem' }}
          >
            <h3>You aren't in any leagues!</h3>
            <p>Get started by registering for a league.</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/register')}
              data-track="navigation.register_for_league"
            >
              Register for a League
            </button>
          </div>
        )}
      </div>
    </PageLayout>
  )
}

export default MyLeagues
